import React, { useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import useBaseService from '../../../../hooks/services/useBaseService'
import useLoader from '../../../../hooks/useLoader'
import useForm from '../../../../hooks/useForm'
import {
  cleanPayload,
  normalizeFormErrors,
  setDefaultFieldValues,
} from '../../../../utils/objectUtils'
import MainLayout from '../../../../layouts/MainLayout'
import PageHeader from '../../../../components/PageHeader'
import Form from '../../../../components/forms/Form'
import SectionHeader from '../../../../components/forms/SectionHeader'
import SimpleBody from '../../../../components/forms/SimpleBody'
import { entities } from '../../../../constants/entities'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import useUrlFilters from '../../../../hooks/useUrlFilters'
import useRemoteSelectOptions from '../../../../hooks/useRemoteSelectOptions'
import { groupFields } from '../../../../constants/forms/almoxarifado/groupFields'

const CreateGroupPage = () => {
  const navigate = useNavigate()
  const {
    post: create,
    get: baseServiceGet,
    formErrors,
    setFormErrors,
  } = useBaseService(navigate)
  const { showLoader, hideLoader } = useLoader()

  const [groupSection] = groupFields

  const { formData, handleChange, resetForm, setFormData } = useForm(
    setDefaultFieldValues([groupSection]),
    [groupSection]
  )
  const queryClient = useQueryClient()

  const { filters, pagination } = useUrlFilters(
    {
      id: [],
      name: [],
      deleted_at: false,
    },
    'almox_groups'
  )

  const typeSelect = useRemoteSelectOptions({
    queryKey: ['types'],
    columnDataBase: ['id', 'name'],
    baseServiceGet,
    entityPath: entities.types.get,
    fieldId: 'group.types',
    formData,
    isMulti: true,
    mapToOption: (item) => ({
      value: item.id,
      label: `${item.id}- ${item.name}`,
    }),
    setFormData,
    perPage: 15,
    enablePagination: true,
  })

  const selects = useMemo(
    () => ({
      'group.types': typeSelect,
    }),
    [typeSelect]
  )

  const getSelectMeta = useCallback(
    (fieldId) => {
      const selectConfig = selects[fieldId]
      if (!selectConfig) return {}

      return {
        hasNextPage: selectConfig.hasNextPage,
        isFetchingNextPage: selectConfig.isFetchingNextPage,
        isLoading: selectConfig.isLoading,
        isFetching: selectConfig.isFetching,
        handleMenuScrollToBottom: selectConfig.handleMenuScrollToBottom,
        handleSearch: selectConfig.handleSearch,
        onFocus: selectConfig.activateQuery,
      }
    },
    [selects]
  )

  const getOptions = useCallback(
    (fieldId) => {
      const selectConfig = selects[fieldId]
      return selectConfig?.options || []
    },
    [selects]
  )

  const getSelectedValue = useCallback(
    (fieldId) => {
      const selectConfig = selects[fieldId]
      return selectConfig?.selectedValue || null
    },
    [selects]
  )

  const createGroupMutation = useMutation({
    mutationFn: async (data) => {
      showLoader()
      const response = await create(entities.groups.create, data)
      return response
    },
    onSuccess: (response) => {
      if (response?.status === 201) {
        hideLoader()
        resetForm()
        navigate('/almoxarifado/produtos/grupos')
        queryClient.invalidateQueries({
          queryKey: ['almox_groups', filters, pagination],
        })
      }
    },
    onError: (error) => {
      console.log('onError ', error)
      if (error.response?.data?.errors) {
        setFormErrors(error.response.data.errors)
      }
      hideLoader()
    },
    onSettled: () => {
      hideLoader()
    },
  })

  const handleSubmit = useCallback(async () => {
    try {
      const cleanedGroup = cleanPayload(formData)
      const typesIds = (formData.group?.types || []).map((t) =>
        typeof t === 'object' && t !== null ? t.value : t
      )
      console.log('typesIds', typesIds)
      const payload = {
        group: {
          ...cleanedGroup.group,
          types: typesIds,
        },
      }
      await createGroupMutation.mutateAsync(payload)
    } catch (error) {
      console.log('Erro ao enviar grupo:', {
        status: error.status,
        message: error.message,
      })
    }
  }, [formData, createGroupMutation])

  const handleBack = useCallback(() => {
    navigate('/almoxarifado/produtos/grupos')
  }, [navigate])

  return (
    <MainLayout selectedCompany="ALUCOM">
      <PageHeader
        title="Cadastro de Grupo"
        showBackButton={true}
        backUrl="/almoxarifado/produtos/grupos"
      />
      <div className="container-fluid p-1">
        <Form
          onSubmit={handleSubmit}
          initialFormData={formData}
          className="p-3 mt-2 rounded shadow-sm mb-2"
          textSubmit="Cadastrar Grupo"
          textLoadingSubmit="Cadastrando..."
          handleBack={handleBack}>
          {() => {
            const renderedSections = new Set()

            return [groupSection].map((section, index) => {
              const sectionType = section.section.split(' - ')[0]
              const showHeader = !renderedSections.has(sectionType)

              if (showHeader) renderedSections.add(sectionType)

              return (
                <div key={section.section + ' ' + index}>
                  {showHeader && (
                    <SectionHeader
                      section={section.section}
                      viewTable={false}
                      setViewTable={() => {}}
                      addFieldsInData={() => {}}
                    />
                  )}

                  <SimpleBody
                    fields={section.fields}
                    formErrors={normalizeFormErrors(formErrors)}
                    formData={formData}
                    handleFieldChange={handleChange}
                    getOptions={getOptions}
                    getSelectedValue={getSelectedValue}
                    getSelectMeta={getSelectMeta}
                  />
                </div>
              )
            })
          }}
        </Form>
      </div>
    </MainLayout>
  )
}

export default CreateGroupPage
